/**
 * PourLimitsSection - Manage Pour2Limit (mass concrete) and
 * Pour2EvaporationLimit records for a pour.
 *
 * Both sub-tables live in a single section, separated by Typography headers.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  MenuItem,
  Alert,
  Paper,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  poursAPI,
  type Pour2Limit,
  type Pour2LimitCreate,
  type Pour2EvaporationLimit,
  type Pour2EvaporationLimitCreate,
  type PourLookups,
} from '../../services/pours';
import { apiErrorText } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PourLimitsSectionProps {
  pourId: string;
  mode: 'create' | 'edit' | 'view';
  lookups: PourLookups;
}

// ---------------------------------------------------------------------------
// Form types
// ---------------------------------------------------------------------------

interface MassLimitFormState {
  MassConcreteDefaultsID: string;
  Criteria: string;
  Purpose: string;
  LimitsType: string;
  LimitsTemp: string;
}

interface EvaLimitFormState {
  EvaporationLimitID: string;
  Purpose: string;
  LimitsType: string;
  LimitsRate: string;
}

const EMPTY_MASS_FORM: MassLimitFormState = {
  MassConcreteDefaultsID: '',
  Criteria: '',
  Purpose: '',
  LimitsType: 'Upper',
  LimitsTemp: '',
};

const EMPTY_EVA_FORM: EvaLimitFormState = {
  EvaporationLimitID: '',
  Purpose: '',
  LimitsType: 'Upper',
  LimitsRate: '',
};

const LIMITS_TYPE_OPTIONS = ['Upper', 'Lower'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PourLimitsSection: React.FC<PourLimitsSectionProps> = ({
  pourId,
  mode,
  lookups,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isViewMode = mode === 'view';

  // ---- Mass concrete limits state ----
  const [massLimits, setMassLimits] = useState<Pour2Limit[]>([]);
  const [massLoading, setMassLoading] = useState(false);

  const [massDialogOpen, setMassDialogOpen] = useState(false);
  const [editingMass, setEditingMass] = useState<Pour2Limit | null>(null);
  const [massForm, setMassForm] = useState<MassLimitFormState>(EMPTY_MASS_FORM);
  const [massSaving, setMassSaving] = useState(false);

  const [massDeleteOpen, setMassDeleteOpen] = useState(false);
  const [massToDelete, setMassToDelete] = useState<Pour2Limit | null>(null);
  const [massDeleting, setMassDeleting] = useState(false);

  // ---- Evaporation limits state ----
  const [evaLimits, setEvaLimits] = useState<Pour2EvaporationLimit[]>([]);
  const [evaLoading, setEvaLoading] = useState(false);

  const [evaDialogOpen, setEvaDialogOpen] = useState(false);
  const [editingEva, setEditingEva] = useState<Pour2EvaporationLimit | null>(null);
  const [evaForm, setEvaForm] = useState<EvaLimitFormState>(EMPTY_EVA_FORM);
  const [evaSaving, setEvaSaving] = useState(false);

  const [evaDeleteOpen, setEvaDeleteOpen] = useState(false);
  const [evaToDelete, setEvaToDelete] = useState<Pour2EvaporationLimit | null>(null);
  const [evaDeleting, setEvaDeleting] = useState(false);

  // ---- Shared state ----
  const [error, setError] = useState<string | null>(null);

  // =====================================================================
  // Data loading
  // =====================================================================

  const loadMassLimits = useCallback(async () => {
    if (!pourId) return;
    setMassLoading(true);
    try {
      const data = await poursAPI.getPourLimits(pourId);
      setMassLimits(data);
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to load mass concrete limits'));
    } finally {
      setMassLoading(false);
    }
  }, [pourId]);

  const loadEvaLimits = useCallback(async () => {
    if (!pourId) return;
    setEvaLoading(true);
    try {
      const data = await poursAPI.getPourEvaporationLimits(pourId);
      setEvaLimits(data);
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to load evaporation limits'));
    } finally {
      setEvaLoading(false);
    }
  }, [pourId]);

  useEffect(() => {
    loadMassLimits();
    loadEvaLimits();
  }, [loadMassLimits, loadEvaLimits]);

  // =====================================================================
  // Mass concrete limit handlers
  // =====================================================================

  const handleMassOpenAdd = () => {
    setEditingMass(null);
    setMassForm(EMPTY_MASS_FORM);
    setMassDialogOpen(true);
  };

  const handleMassOpenEdit = (limit: Pour2Limit) => {
    setEditingMass(limit);
    setMassForm({
      MassConcreteDefaultsID: limit.MassConcreteDefaultsID || '',
      Criteria: limit.Criteria || '',
      Purpose: limit.Purpose || '',
      LimitsType: limit.LimitsType || 'Upper',
      LimitsTemp: limit.LimitsTemp != null ? String(limit.LimitsTemp) : '',
    });
    setMassDialogOpen(true);
  };

  const handleMassCloseDialog = () => {
    setMassDialogOpen(false);
    setEditingMass(null);
    setMassForm(EMPTY_MASS_FORM);
  };

  const handleMassFormChange = (field: keyof MassLimitFormState, value: string) => {
    setMassForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMassSave = async () => {
    if (!massForm.Criteria.trim() || !massForm.Purpose.trim() || !massForm.LimitsTemp.trim()) return;

    const payload: Pour2LimitCreate = {
      MassConcreteDefaultsID: massForm.MassConcreteDefaultsID,
      Criteria: massForm.Criteria.trim(),
      Purpose: massForm.Purpose.trim(),
      LimitsType: massForm.LimitsType,
      LimitsTemp: parseFloat(massForm.LimitsTemp),
    };

    setMassSaving(true);
    setError(null);
    try {
      if (editingMass) {
        await poursAPI.updatePourLimit(pourId, editingMass.ID, payload);
      } else {
        await poursAPI.addPourLimit(pourId, payload);
      }
      handleMassCloseDialog();
      await loadMassLimits();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to save mass concrete limit'));
    } finally {
      setMassSaving(false);
    }
  };

  const handleMassDeleteClick = (limit: Pour2Limit) => {
    setMassToDelete(limit);
    setMassDeleteOpen(true);
  };

  const handleMassDeleteConfirm = async () => {
    if (!massToDelete) return;
    setMassDeleting(true);
    setError(null);
    try {
      await poursAPI.deletePourLimit(pourId, massToDelete.ID);
      setMassDeleteOpen(false);
      setMassToDelete(null);
      await loadMassLimits();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to delete mass concrete limit'));
    } finally {
      setMassDeleting(false);
    }
  };

  const handleMassDeleteCancel = () => {
    setMassDeleteOpen(false);
    setMassToDelete(null);
  };

  // =====================================================================
  // Evaporation limit handlers
  // =====================================================================

  const handleEvaOpenAdd = () => {
    setEditingEva(null);
    setEvaForm(EMPTY_EVA_FORM);
    setEvaDialogOpen(true);
  };

  const handleEvaOpenEdit = (limit: Pour2EvaporationLimit) => {
    setEditingEva(limit);
    setEvaForm({
      EvaporationLimitID: limit.EvaporationLimitID || '',
      Purpose: limit.Purpose || '',
      LimitsType: limit.LimitsType || 'Upper',
      LimitsRate: limit.LimitsRate != null ? String(limit.LimitsRate) : '',
    });
    setEvaDialogOpen(true);
  };

  const handleEvaCloseDialog = () => {
    setEvaDialogOpen(false);
    setEditingEva(null);
    setEvaForm(EMPTY_EVA_FORM);
  };

  const handleEvaFormChange = (field: keyof EvaLimitFormState, value: string) => {
    setEvaForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEvaSave = async () => {
    if (!evaForm.Purpose.trim() || !evaForm.LimitsRate.trim()) return;

    const payload: Pour2EvaporationLimitCreate = {
      EvaporationLimitID: evaForm.EvaporationLimitID,
      Purpose: evaForm.Purpose.trim(),
      LimitsType: evaForm.LimitsType,
      LimitsRate: parseFloat(evaForm.LimitsRate),
    };

    setEvaSaving(true);
    setError(null);
    try {
      if (editingEva) {
        await poursAPI.updatePourEvaporationLimit(pourId, editingEva.ID, payload);
      } else {
        await poursAPI.addPourEvaporationLimit(pourId, payload);
      }
      handleEvaCloseDialog();
      await loadEvaLimits();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to save evaporation limit'));
    } finally {
      setEvaSaving(false);
    }
  };

  const handleEvaDeleteClick = (limit: Pour2EvaporationLimit) => {
    setEvaToDelete(limit);
    setEvaDeleteOpen(true);
  };

  const handleEvaDeleteConfirm = async () => {
    if (!evaToDelete) return;
    setEvaDeleting(true);
    setError(null);
    try {
      await poursAPI.deletePourEvaporationLimit(pourId, evaToDelete.ID);
      setEvaDeleteOpen(false);
      setEvaToDelete(null);
      await loadEvaLimits();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to delete evaporation limit'));
    } finally {
      setEvaDeleting(false);
    }
  };

  const handleEvaDeleteCancel = () => {
    setEvaDeleteOpen(false);
    setEvaToDelete(null);
  };

  // =====================================================================
  // DataGrid column definitions
  // =====================================================================

  const massColumns: GridColDef[] = [
    {
      field: 'Criteria',
      headerName: t('pourLimits.criteria', 'Criteria'),
      flex: 1.2,
      minWidth: 130,
    },
    {
      field: 'Purpose',
      headerName: t('pourLimits.purpose', 'Purpose'),
      flex: 1.2,
      minWidth: 130,
    },
    {
      field: 'LimitsType',
      headerName: t('pourLimits.type', 'Type'),
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: 'LimitsTemp',
      headerName: t('pourLimits.temp', 'Temp'),
      flex: 1,
      minWidth: 100,
      type: 'number',
      valueFormatter: (value: number | string | null) =>
        value != null ? `${Number(value).toFixed(1)} C` : '-',
    },
    ...(!isViewMode
      ? [
          {
            field: 'actions' as const,
            type: 'actions' as const,
            headerName: t('pourLimits.actions', 'Actions'),
            width: 100,
            minWidth: 100,
            getActions: (params: GridRowParams<Pour2Limit>) => [
              <GridActionsCellItem
                icon={<EditIcon />}
                label={t('pourLimits.edit', 'Edit')}
                onClick={() => handleMassOpenEdit(params.row)}
                color="primary"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={t('pourLimits.delete', 'Delete')}
                onClick={() => handleMassDeleteClick(params.row)}
                color="inherit"
              />,
            ],
          },
        ]
      : []),
  ];

  const evaColumns: GridColDef[] = [
    {
      field: 'Purpose',
      headerName: t('pourLimits.purpose', 'Purpose'),
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: 'LimitsType',
      headerName: t('pourLimits.type', 'Type'),
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: 'LimitsRate',
      headerName: t('pourLimits.rate', 'Rate'),
      flex: 1,
      minWidth: 120,
      type: 'number',
      valueFormatter: (value: number | string | null) =>
        value != null ? `${Number(value).toFixed(2)}` : '-',
    },
    ...(!isViewMode
      ? [
          {
            field: 'actions' as const,
            type: 'actions' as const,
            headerName: t('pourLimits.actions', 'Actions'),
            width: 100,
            minWidth: 100,
            getActions: (params: GridRowParams<Pour2EvaporationLimit>) => [
              <GridActionsCellItem
                icon={<EditIcon />}
                label={t('pourLimits.edit', 'Edit')}
                onClick={() => handleEvaOpenEdit(params.row)}
                color="primary"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={t('pourLimits.delete', 'Delete')}
                onClick={() => handleEvaDeleteClick(params.row)}
                color="inherit"
              />,
            ],
          },
        ]
      : []),
  ];

  // =====================================================================
  // Mobile card renderers
  // =====================================================================

  const renderMassCard = (limit: Pour2Limit) => (
    <Card key={limit.ID} elevation={1}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {limit.Purpose}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t('pourLimits.criteria', 'Criteria')}: {limit.Criteria || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {limit.LimitsType} &mdash; {limit.LimitsTemp?.toFixed(1) ?? '-'} C
            </Typography>
          </Box>
          {!isViewMode && (
            <Box>
              <IconButton size="small" color="primary" onClick={() => handleMassOpenEdit(limit)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="inherit" onClick={() => handleMassDeleteClick(limit)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderEvaCard = (limit: Pour2EvaporationLimit) => (
    <Card key={limit.ID} elevation={1}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {limit.Purpose}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {limit.LimitsType} &mdash; {t('pourLimits.rate', 'Rate')}: {limit.LimitsRate?.toFixed(2) ?? '-'}
            </Typography>
          </Box>
          {!isViewMode && (
            <Box>
              <IconButton size="small" color="primary" onClick={() => handleEvaOpenEdit(limit)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="inherit" onClick={() => handleEvaDeleteClick(limit)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // =====================================================================
  // Render helpers for DataGrid / mobile lists
  // =====================================================================

  const renderDataList = (
    items: readonly any[],
    cols: GridColDef[],
    isLoading: boolean,
    emptyLabel: string,
    renderCard: (item: any) => React.ReactNode,
  ) => {
    if (isMobile) {
      return (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : items.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              {emptyLabel}
            </Typography>
          ) : (
            items.map(renderCard)
          )}
        </Box>
      );
    }

    return (
      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={items}
          columns={cols}
          getRowId={(row) => row.ID}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 5 } },
          }}
          autoHeight
          disableRowSelectionOnClick
          loading={isLoading}
          sx={{ border: 0 }}
          slotProps={{
            pagination: {
              labelRowsPerPage: 'Rows per page:',
            },
          }}
        />
      </Paper>
    );
  };

  // =====================================================================
  // Render
  // =====================================================================

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ---- Mass Concrete Limits ---- */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          {t('pourLimits.massConcreteLimits', 'Mass Concrete Limits')}
        </Typography>
        {!isViewMode && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleMassOpenAdd}
            disabled={massLoading}
          >
            {t('pourLimits.addLimit', 'Add Limit')}
          </Button>
        )}
      </Box>

      {renderDataList(
        massLimits,
        massColumns,
        massLoading,
        t('pourLimits.noMassLimits', 'No mass concrete limits defined yet'),
        renderMassCard,
      )}

      {/* ---- Evaporation Limits ---- */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={2}>
        <Typography variant="h6">
          {t('pourLimits.evaporationLimits', 'Evaporation Limits')}
        </Typography>
        {!isViewMode && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleEvaOpenAdd}
            disabled={evaLoading}
          >
            {t('pourLimits.addLimit', 'Add Limit')}
          </Button>
        )}
      </Box>

      {renderDataList(
        evaLimits,
        evaColumns,
        evaLoading,
        t('pourLimits.noEvaLimits', 'No evaporation limits defined yet'),
        renderEvaCard,
      )}

      {/* ================================================================ */}
      {/* Mass Concrete Limit - Add / Edit Dialog                          */}
      {/* ================================================================ */}
      <Dialog open={massDialogOpen} onClose={handleMassCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMass
            ? t('pourLimits.editMassLimit', 'Edit Mass Concrete Limit')
            : t('pourLimits.addMassLimit', 'Add Mass Concrete Limit')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              select
              label={t('pourLimits.massConcreteDefault', 'Mass Concrete Default')}
              value={massForm.MassConcreteDefaultsID}
              onChange={(e) => handleMassFormChange('MassConcreteDefaultsID', e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                {t('pourLimits.noneOption', '-- None --')}
              </MenuItem>
              {lookups.mass_concrete_defaults.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('pourLimits.criteria', 'Criteria')}
              value={massForm.Criteria}
              onChange={(e) => handleMassFormChange('Criteria', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('pourLimits.purpose', 'Purpose')}
              value={massForm.Purpose}
              onChange={(e) => handleMassFormChange('Purpose', e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label={t('pourLimits.limitsType', 'Limits Type')}
              value={massForm.LimitsType}
              onChange={(e) => handleMassFormChange('LimitsType', e.target.value)}
              fullWidth
              required
            >
              {LIMITS_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('pourLimits.temperatureLimit', 'Temperature Limit')}
              value={massForm.LimitsTemp}
              onChange={(e) => handleMassFormChange('LimitsTemp', e.target.value)}
              fullWidth
              required
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">C</InputAdornment>,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMassCloseDialog} disabled={massSaving}>
            {t('pourLimits.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleMassSave}
            variant="contained"
            disabled={
              massSaving ||
              !massForm.Criteria.trim() ||
              !massForm.Purpose.trim() ||
              !massForm.LimitsTemp.trim()
            }
            startIcon={massSaving ? <CircularProgress size={16} /> : undefined}
          >
            {editingMass ? t('pourLimits.save', 'Save') : t('pourLimits.add', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mass Concrete Limit - Delete Confirmation */}
      <Dialog open={massDeleteOpen} onClose={handleMassDeleteCancel}>
        <DialogTitle>{t('pourLimits.confirmDelete', 'Confirm Delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('pourLimits.confirmDeleteMassMessage', 'Are you sure you want to delete this mass concrete limit?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMassDeleteCancel} disabled={massDeleting}>
            {t('pourLimits.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleMassDeleteConfirm}
            color="inherit"
            variant="contained"
            disabled={massDeleting}
            startIcon={massDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {t('pourLimits.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================ */}
      {/* Evaporation Limit - Add / Edit Dialog                            */}
      {/* ================================================================ */}
      <Dialog open={evaDialogOpen} onClose={handleEvaCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEva
            ? t('pourLimits.editEvaLimit', 'Edit Evaporation Limit')
            : t('pourLimits.addEvaLimit', 'Add Evaporation Limit')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              select
              label={t('pourLimits.evaporationDefault', 'Evaporation Default')}
              value={evaForm.EvaporationLimitID}
              onChange={(e) => handleEvaFormChange('EvaporationLimitID', e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                {t('pourLimits.noneOption', '-- None --')}
              </MenuItem>
              {lookups.evaporation_defaults.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('pourLimits.purpose', 'Purpose')}
              value={evaForm.Purpose}
              onChange={(e) => handleEvaFormChange('Purpose', e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label={t('pourLimits.limitsType', 'Limits Type')}
              value={evaForm.LimitsType}
              onChange={(e) => handleEvaFormChange('LimitsType', e.target.value)}
              fullWidth
              required
            >
              {LIMITS_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('pourLimits.rateLimit', 'Rate Limit')}
              value={evaForm.LimitsRate}
              onChange={(e) => handleEvaFormChange('LimitsRate', e.target.value)}
              fullWidth
              required
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEvaCloseDialog} disabled={evaSaving}>
            {t('pourLimits.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleEvaSave}
            variant="contained"
            disabled={evaSaving || !evaForm.Purpose.trim() || !evaForm.LimitsRate.trim()}
            startIcon={evaSaving ? <CircularProgress size={16} /> : undefined}
          >
            {editingEva ? t('pourLimits.save', 'Save') : t('pourLimits.add', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Evaporation Limit - Delete Confirmation */}
      <Dialog open={evaDeleteOpen} onClose={handleEvaDeleteCancel}>
        <DialogTitle>{t('pourLimits.confirmDelete', 'Confirm Delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('pourLimits.confirmDeleteEvaMessage', 'Are you sure you want to delete this evaporation limit?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEvaDeleteCancel} disabled={evaDeleting}>
            {t('pourLimits.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleEvaDeleteConfirm}
            color="inherit"
            variant="contained"
            disabled={evaDeleting}
            startIcon={evaDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {t('pourLimits.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PourLimitsSection;
